import React, { useCallback, useEffect, useRef, useState } from "react"
import "./VideoPlayer.css"

// how long the controls stay up after the last mouse move / tap while playing
const CONTROLS_HIDE_DELAY_MS = 2500
const SEEK_STEP_SECONDS = 5

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return "0:00"
    const s = Math.floor(seconds)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}

const volumeIcon = (volume, muted) => {
    if (muted || volume === 0) return "fas fa-volume-mute"
    return volume < 0.5 ? "fas fa-volume-down" : "fas fa-volume-up"
}

// YouTube-style player: big center play/pause, seek bar, play/pause + volume + time
// bottom-left, fullscreen bottom-right. Controls show on hover/tap and while paused.
export const VideoPlayer = ({ src, label }) => {
    const containerRef = useRef(null)
    const videoRef = useRef(null)
    const hideTimer = useRef(null)

    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [volume, setVolume] = useState(1)
    const [muted, setMuted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [controlsActive, setControlsActive] = useState(false)

    const showControls = useCallback(() => {
        setControlsActive(true)
        clearTimeout(hideTimer.current)
        hideTimer.current = setTimeout(() => setControlsActive(false), CONTROLS_HIDE_DELAY_MS)
    }, [])

    useEffect(() => () => clearTimeout(hideTimer.current), [])

    useEffect(() => {
        const onChange = () => setIsFullscreen(
            (document.fullscreenElement || document.webkitFullscreenElement) === containerRef.current
        )
        document.addEventListener("fullscreenchange", onChange)
        document.addEventListener("webkitfullscreenchange", onChange)
        return () => {
            document.removeEventListener("fullscreenchange", onChange)
            document.removeEventListener("webkitfullscreenchange", onChange)
        }
    }, [])

    const togglePlay = () => {
        const video = videoRef.current
        if (video.paused || video.ended) {
            video.play()
        } else {
            video.pause()
        }
        showControls()
    }

    const seekTo = (seconds) => {
        const video = videoRef.current
        video.currentTime = Math.min(Math.max(seconds, 0), duration || 0)
        setCurrentTime(video.currentTime)
    }

    const changeVolume = (value) => {
        const video = videoRef.current
        video.volume = value
        video.muted = value === 0
    }

    const toggleMute = () => {
        const video = videoRef.current
        if (video.muted || video.volume === 0) {
            // unmuting from a zero volume should bring the sound back, not stay silent
            if (video.volume === 0) video.volume = 0.5
            video.muted = false
        } else {
            video.muted = true
        }
    }

    const toggleFullscreen = () => {
        const container = containerRef.current
        const video = videoRef.current
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            (document.exitFullscreen || document.webkitExitFullscreen).call(document)
        } else if (container.requestFullscreen) {
            container.requestFullscreen()
        } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen()
        } else if (video.webkitEnterFullscreen) {
            // iPhone Safari only allows the native video element to go fullscreen
            video.webkitEnterFullscreen()
        }
    }

    const onKeyDown = (event) => {
        const key = event.key.toLowerCase()
        if (key === " " || key === "k") togglePlay()
        else if (key === "f") toggleFullscreen()
        else if (key === "m") toggleMute()
        else if (key === "arrowright") seekTo(currentTime + SEEK_STEP_SECONDS)
        else if (key === "arrowleft") seekTo(currentTime - SEEK_STEP_SECONDS)
        else return
        event.preventDefault()
        showControls()
    }

    // mouse: clicking the picture plays/pauses; touch: the first tap only reveals the controls
    const onVideoPointerUp = (event) => {
        if (event.pointerType === "mouse") {
            togglePlay()
        } else if (controlsActive) {
            setControlsActive(false)
        } else {
            showControls()
        }
    }

    const progress = duration ? (currentTime / duration) * 100 : 0
    const volumeLevel = muted ? 0 : volume
    const controlsVisible = !isPlaying || controlsActive

    return (
        <div
            ref={containerRef}
            className={`lf-vp ${controlsVisible ? "lf-vp--controls" : ""} ${isFullscreen ? "lf-vp--fullscreen" : ""}`}
            tabIndex={0}
            aria-label={label}
            onMouseMove={showControls}
            onMouseLeave={() => setControlsActive(false)}
            onKeyDown={onKeyDown}
        >
            <video
                ref={videoRef}
                preload="metadata"
                playsInline
                onPointerUp={onVideoPointerUp}
                onDoubleClick={toggleFullscreen}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onLoadedMetadata={e => setDuration(e.target.duration)}
                onDurationChange={e => setDuration(e.target.duration)}
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                onVolumeChange={e => {
                    setVolume(e.target.volume)
                    setMuted(e.target.muted)
                }}
            >
                {/* #t jumps to the first frame so it shows before playing */}
                <source src={`${src}#t=0.1`} type="video/mp4" />
            </video>

            <button
                className="lf-vp__center"
                onClick={togglePlay}
                aria-label={isPlaying ? "Pause" : "Play"}
                tabIndex={-1}
            >
                <i className={isPlaying ? "fas fa-pause" : "fas fa-play"}></i>
            </button>

            <div className="lf-vp__bar">
                <input
                    className="lf-vp__seek"
                    type="range"
                    min="0"
                    max={duration || 0}
                    step="0.1"
                    value={currentTime}
                    onChange={e => seekTo(Number(e.target.value))}
                    style={{ "--fill": `${progress}%` }}
                    aria-label="Seek"
                />

                <div className="lf-vp__row">
                    <button className="lf-vp__btn" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                        <i className={isPlaying ? "fas fa-pause" : "fas fa-play"}></i>
                    </button>

                    <div className="lf-vp__volume">
                        <button className="lf-vp__btn" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                            <i className={volumeIcon(volume, muted)}></i>
                        </button>
                        <input
                            className="lf-vp__volumeSlider"
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={volumeLevel}
                            onChange={e => changeVolume(Number(e.target.value))}
                            style={{ "--fill": `${volumeLevel * 100}%` }}
                            aria-label="Volume"
                        />
                    </div>

                    <span className="lf-vp__time">{formatTime(currentTime)} / {formatTime(duration)}</span>

                    <button
                        className="lf-vp__btn lf-vp__fullscreen"
                        onClick={toggleFullscreen}
                        aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
                    >
                        <i className={isFullscreen ? "fas fa-compress" : "fas fa-expand"}></i>
                    </button>
                </div>
            </div>
        </div>
    )
}
